/* Goldroot shared utilities. Every other component imports behaviour from here
   rather than re-implementing it, so there is exactly one money formatter, one
   fetch wrapper and one announcer in the theme. */

window.GR = window.GR || {};

/* ---------- Money ----------
   Prices cross the JS boundary as integer subunits, never as floats and never
   as pre-formatted strings, so arithmetic stays exact and formatting stays
   Markets-aware. */
GR.money = function (cents, format) {
  const fmt = format || GR.moneyFormat || '${{amount}}';
  const value = Math.round(Number(cents) || 0);

  function withDelimiters(num, precision, thousands, decimal) {
    thousands = thousands || ',';
    decimal = decimal || '.';
    const fixed = (num / 100.0).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    const cents_ = parts[1] ? decimal + parts[1] : '';
    return dollars + cents_;
  }

  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, name) {
    switch (name) {
      case 'amount': return withDelimiters(value, 2, ',', '.');
      case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', '.');
      case 'amount_with_space_separator': return withDelimiters(value, 2, ' ', ',');
      default: return withDelimiters(value, 2, ',', '.');
    }
  });
};

/* Locale-aware route builder. Nothing in the theme may hardcode /cart/add.js —
   that would drop a shopper out of their market on every mutation. */
GR.url = function (path) {
  const root = (GR.routes && GR.routes.root) || '/';
  return (root.endsWith('/') ? root.slice(0, -1) : root) + path;
};

GR.announce = function (message) {
  const region = document.getElementById('gr-live-region');
  if (!region || !message) return;
  // Clearing first guarantees screen readers re-announce an identical string.
  region.textContent = '';
  window.setTimeout(function () { region.textContent = message; }, 60);
};

/* Single fetch wrapper: every cart mutation goes through this, so error
   handling, abort and JSON parsing behave identically everywhere. */
GR.fetchJSON = async function (url, options) {
  const opts = Object.assign({ headers: {} }, options || {});
  opts.headers = Object.assign(
    { 'Content-Type': 'application/json', Accept: 'application/json' },
    opts.headers
  );

  const response = await fetch(url, opts);
  const text = await response.text();

  let data = null;
  try { data = text ? JSON.parse(text) : null; }
  catch (e) {
    // A non-JSON body from a cart endpoint is always a real failure.
    const err = new Error('Malformed response');
    err.status = response.status;
    throw err;
  }

  if (!response.ok) {
    const err = new Error((data && (data.description || data.message)) || 'Request failed');
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

/* Focus trap shared by both drawers. Returns a teardown function; callers must
   invoke it on close or the listener leaks across Theme Editor re-renders. */
GR.trapFocus = function (container, initialFocus) {
  const SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function focusable() {
    return Array.from(container.querySelectorAll(SELECTOR)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function onKeydown(event) {
    if (event.key !== 'Tab') return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  container.addEventListener('keydown', onKeydown);
  const target = initialFocus || focusable()[0];
  if (target) window.setTimeout(function () { target.focus(); }, 50);

  return function release() { container.removeEventListener('keydown', onKeydown); };
};

/* Body scroll lock is refcounted — opening the cart drawer from inside the
   mobile nav must not unlock the page when only one of them closes. */
GR._lockCount = 0;
GR.lockScroll = function () {
  GR._lockCount += 1;
  document.body.classList.add('gr-scroll-locked');
};
GR.unlockScroll = function () {
  GR._lockCount = Math.max(0, GR._lockCount - 1);
  if (GR._lockCount === 0) document.body.classList.remove('gr-scroll-locked');
};

/* Business-day ships-by date. Computed client-side because a server-rendered
   date goes stale the moment Shopify caches the page. */
GR.shipsBy = function (leadDays) {
  const date = new Date();
  let added = 0;
  const target = Number(leadDays);
  const days = isNaN(target) ? 2 : target;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return date;
};

GR.formatShipsBy = function (date, style) {
  const locale = (GR.locale || 'en').replace('_', '-');
  const opts = style === 'long'
    ? { weekday: 'long' }
    : { weekday: 'short', day: 'numeric', month: 'short' };
  try { return new Intl.DateTimeFormat(locale, opts).format(date); }
  catch (e) { return new Intl.DateTimeFormat('en-GB', opts).format(date); }
};

/* Any element carrying [data-ships-by] fills itself in on load. */
function paintShipsBy(root) {
  (root || document).querySelectorAll('[data-ships-by]').forEach(function (el) {
    const lead = el.getAttribute('data-lead-days');
    const style = el.getAttribute('data-ships-by');
    el.textContent = GR.formatShipsBy(GR.shipsBy(lead), style);
  });
}

document.addEventListener('DOMContentLoaded', function () { paintShipsBy(); });
document.addEventListener('shopify:section:load', function (event) { paintShipsBy(event.target); });
GR.paintShipsBy = paintShipsBy;
